<template>
  <div class="bot-chats">
    <p class="grey--text mt-4">
      Усі, хто написав у Telegram-бот Платформи<template v-if="botUsername"> (<a :href="'https://t.me/' + botUsername" target="_blank" rel="noopener">@{{ botUsername }}</a>)</template>:
      психологи і клієнти. Помічник відповідає за базою знань із розділу «ШІ-агент». Що не може вирішити, передає Вам: Вам приходить лист, а чат позначається «Потрібна відповідь».
    </p>
    <div v-if="loaded && botReady" class="bot-chats__operator">
      <template v-if="operator">
        <v-icon small color="green">mdi-check-circle</v-icon>
        Усі повідомлення бота приходять і у Ваш Telegram: <b>{{ operator }}</b>. Відповідайте там через «Відповісти» на повідомленні людини.
        <v-btn x-small text color="grey" @click="disconnectOperator">Відключити</v-btn>
      </template>
      <template v-else>
        <v-btn small depressed color="primary" :loading="operatorLoading" @click="connectOperator">
          <v-icon left small>mdi-send</v-icon>Отримувати чати в мій Telegram
        </v-btn>
        <span class="grey--text">Один загальний чат на робочому телефоні: туди приходять усі питання, відповідати можна прямо там.</span>
      </template>
      <p v-if="operatorLink" class="bot-chats__operator-link">
        Відкрийте це посилання на робочому телефоні і натисніть «Почати» (діє годину):
        <a :href="operatorLink" target="_blank" rel="noopener">{{ operatorLink }}</a>
        <v-btn x-small text color="primary" @click="checkOperator">Я підключила</v-btn>
      </p>
    </div>
    <p v-if="loaded && !botReady" class="red--text">Токен бота на сервері не задано: бот не приймає повідомлень.</p>
    <p v-else-if="loaded && !aiReady" class="red--text">Ключ OpenAI на сервері не задано: помічник не відповідає.</p>

    <div class="bot-chats__toolbar">
      <v-switch
        v-model="aiEnabled"
        class="mt-0 pt-0"
        hide-details
        label="Помічник відповідає в боті"
        :disabled="!loaded"
        @change="saveAi"
      ></v-switch>
      <v-select v-model="filter" :items="filters" label="Показати" outlined dense hide-details class="bot-chats__filter" @change="load"></v-select>
      <v-text-field v-model.trim="search" label="Пошук за іменем" outlined dense hide-details clearable class="bot-chats__search" @input="searchLater"></v-text-field>
      <v-btn small outlined color="primary" @click="repliesOpen = true">
        <v-icon left small>mdi-file-tree-outline</v-icon>Стандартні відповіді і меню бота
      </v-btn>
    </div>
    <crm-quick-replies v-model="repliesOpen" @changed="loadReplies"></crm-quick-replies>

    <div class="bot-chats__panes" :class="{ 'bot-chats__panes--open': current }">
      <ul class="bot-chats__list">
        <li v-if="loaded && !chats.length" class="bot-chats__empty">Чатів ще немає</li>
        <li v-for="chat in chats" :key="chat.id">
          <button
            type="button"
            class="bot-chats__item"
            :class="{ 'bot-chats__item--active': current && current.id === chat.id }"
            @click="open(chat.id)"
          >
            <span class="bot-chats__line">
              <span class="bot-chats__name">{{ chat.name }}</span>
              <span class="bot-chats__date">{{ short(chat.last_message_at) }}</span>
            </span>
            <span class="bot-chats__line">
              <span class="bot-chats__preview">{{ chat.last_text }}</span>
              <span v-if="chat.unread" class="bot-chats__unread">{{ chat.unread }}</span>
            </span>
            <span class="bot-chats__tags">
              <span class="bot-chats__tag">{{ chat.psychologist ? "Психолог" : "Клієнт" }}</span>
              <span v-if="chat.needs_human" class="bot-chats__tag bot-chats__tag--human">Потрібна відповідь</span>
            </span>
          </button>
        </li>
      </ul>

      <section class="bot-chats__dialog">
        <p v-if="!current" class="bot-chats__hint">Оберіть чат зліва</p>
        <template v-else>
          <header class="bot-chats__head">
            <v-btn icon class="bot-chats__back" aria-label="До списку" @click="current = null"><v-icon>mdi-arrow-left</v-icon></v-btn>
            <div class="bot-chats__who">
              <b>{{ current.name }}</b>
              <span v-if="current.username" class="grey--text">
                <a :href="'https://t.me/' + current.username" target="_blank" rel="noopener">@{{ current.username }}</a>
              </span>
              <span v-if="current.blocked" class="red--text caption">Заблокували бота</span>
            </div>
            <div class="bot-chats__actions">
              <v-btn v-if="current.needs_human" small text color="primary" @click="post('done')">Питання закрито</v-btn>
              <v-btn small text @click="post('assistant', { paused: !current.ai_paused })">
                {{ current.ai_paused ? "Повернути помічника" : "Вимкнути помічника тут" }}
              </v-btn>
            </div>
          </header>

          <div ref="messages" class="bot-chats__messages">
            <div v-for="message in current.messages" :key="message.id" class="bot-chats__msg" :class="'bot-chats__msg--' + message.sender">
              <span class="bot-chats__author">{{ authors[message.sender] }} · {{ human(message.created_at) }}</span>
              <div class="bot-chats__bubble" :class="{ 'bot-chats__bubble--deleted': message.deleted }">{{ message.text }}</div>
              <span v-if="message.deleted" class="caption grey--text">видалено в Telegram</span>
              <span v-else-if="message.edited" class="caption grey--text">змінено</span>
              <span v-if="message.handoff" class="caption orange--text">передано Вам</span>
              <span v-if="message.failed" class="caption red--text">не доставлено</span>
              <span v-if="message.can_edit || message.can_delete" class="bot-chats__msg-actions">
                <button v-if="message.can_edit" type="button" @click="startEdit(message)">Змінити</button>
                <button v-if="message.can_delete" type="button" @click="remove(message)">Видалити</button>
              </span>
            </div>
          </div>

          <p v-if="current.ai_paused" class="bot-chats__paused">Помічник у цьому чаті мовчить. Після Вашої відповіді він вмикається сам через 12 годин.</p>
          <p v-if="editing" class="bot-chats__editing">
            Змінюєте свою відповідь: людина побачить новий текст у тому самому повідомленні.
            <button type="button" @click="cancelEdit">Скасувати</button>
          </p>
          <div class="bot-chats__composer">
            <v-menu v-if="replies.length" top offset-y max-height="360">
              <template #activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" title="Стандартні відповіді" aria-label="Стандартні відповіді" v-on="on"><v-icon>mdi-text-box-plus-outline</v-icon></v-btn>
              </template>
              <v-list dense>
                <v-list-item v-for="item in replies" :key="item.id" @click="insertReply(item)">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.title }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.text }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-textarea
              v-model="reply"
              placeholder="Ваша відповідь"
              outlined
              dense
              auto-grow
              rows="1"
              hide-details
              counter="3500"
              maxlength="3500"
              class="bot-chats__input"
              @keydown.enter.exact.prevent="send"
            ></v-textarea>
            <v-btn color="primary" depressed :loading="sending" :disabled="!reply.trim()" @click="send">{{ editing ? "Зберегти" : "Надіслати" }}</v-btn>
          </div>
          <p v-if="error" class="red--text caption mt-2 mb-0">{{ error }}</p>
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import ApiClient from "@/libs/http-client";
import CrmQuickReplies from "./CrmQuickReplies";

const POLL_MS = 15000;

export default {
  name: "CrmBotChats",
  components: { CrmQuickReplies },
  data() {
    return {
      chats: [],
      current: null,
      loaded: false,
      aiEnabled: true,
      aiReady: true,
      botReady: true,
      botUsername: "",
      filter: "all",
      search: "",
      reply: "",
      sending: false,
      error: "",
      timer: null,
      searchTimer: null,
      filters: [
        { text: "Усі чати", value: "all" },
        { text: "Потрібна відповідь", value: "human" },
        { text: "Психологи", value: "psychologists" },
        { text: "Клієнти", value: "clients" },
      ],
      authors: { user: "Людина", ai: "Помічник", admin: "Ви", bot: "Бот" },
      operator: null,
      operatorLink: "",
      operatorLoading: false,
      editing: null,
      replies: [],
      repliesOpen: false,
    };
  },
  mounted() {
    this.load();
    this.loadReplies();
    this.timer = setInterval(this.refresh, POLL_MS);
  },
  beforeDestroy() {
    clearInterval(this.timer);
    clearTimeout(this.searchTimer);
  },
  methods: {
    load() {
      const params = { filter: this.filter };
      if (this.search) params.search = this.search;

      return ApiClient.get("api/admin/crm/telegram-chats", { params }).then((response) => {
        this.chats = response.data.data;
        const meta = response.data.meta;
        this.aiEnabled = meta.ai_enabled;
        this.aiReady = meta.ai_ready;
        this.botReady = meta.bot_ready;
        this.botUsername = meta.bot_username;
        this.operator = meta.operator;
        if (meta.operator) this.operatorLink = "";
        this.loaded = true;
      });
    },
    // the open chat follows new messages; what the admin is typing stays
    refresh() {
      this.load();
      if (this.current) this.open(this.current.id, true);
    },
    searchLater() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.load, 400);
    },
    open(id, quiet) {
      if (!quiet) this.error = "";

      return ApiClient.get(`api/admin/crm/telegram-chats/${id}`).then((response) => {
        this.show(response.data.data);
      });
    },
    show(chat) {
      const same = this.current && this.current.id === chat.id;
      if (!same) this.cancelEdit();
      const more = !same || this.current.messages.length !== chat.messages.length;
      this.current = chat;
      const item = this.chats.find((row) => row.id === chat.id);
      if (item) Object.assign(item, { unread: 0, needs_human: chat.needs_human });
      if (more) {
        this.$nextTick(() => {
          const box = this.$refs.messages;
          if (box) box.scrollTop = box.scrollHeight;
        });
      }
    },
    send() {
      const text = this.reply.trim();
      if (!text || this.sending) return;
      this.sending = true;
      this.error = "";
      const request = this.editing
        ? ApiClient.put(`api/admin/crm/telegram-chats/${this.current.id}/messages/${this.editing.id}`, { text })
        : ApiClient.post(`api/admin/crm/telegram-chats/${this.current.id}/reply`, { text });
      request
        .then((response) => {
          this.reply = "";
          this.editing = null;
          this.show(response.data.data);
          this.load();
        })
        .catch((error) => {
          this.error = (error && error.message) || "Не вдалося надіслати";
        })
        .finally(() => {
          this.sending = false;
        });
    },
    startEdit(message) {
      this.editing = message;
      this.reply = message.text;
      this.error = "";
    },
    cancelEdit() {
      this.editing = null;
      this.reply = "";
    },
    remove(message) {
      if (!window.confirm("Видалити це повідомлення в Telegram людини?")) return;
      this.error = "";
      ApiClient.delete(`api/admin/crm/telegram-chats/${this.current.id}/messages/${message.id}`)
        .then((response) => this.show(response.data.data))
        .catch((error) => {
          this.error = (error && error.message) || "Не вдалося видалити";
        });
    },
    loadReplies() {
      return ApiClient.get("api/admin/crm/telegram-quick-replies").then((response) => {
        this.replies = response.data.data;
      });
    },
    insertReply(item) {
      this.reply = this.reply.trim() ? `${this.reply.trim()}\n\n${item.text}` : item.text;
    },
    connectOperator() {
      this.operatorLoading = true;
      ApiClient.post("api/admin/crm/telegram-chats/operator/link")
        .then((response) => {
          this.operatorLink = response.data.data.link;
        })
        .catch((error) => {
          this.error = (error && error.message) || "Не вдалося створити посилання";
        })
        .finally(() => {
          this.operatorLoading = false;
        });
    },
    checkOperator() {
      this.load();
    },
    disconnectOperator() {
      if (!window.confirm("Перестати надсилати чати бота у Ваш Telegram?")) return;
      ApiClient.delete("api/admin/crm/telegram-chats/operator").then(() => this.load());
    },
    post(action, body = {}) {
      ApiClient.post(`api/admin/crm/telegram-chats/${this.current.id}/${action}`, body).then((response) => {
        this.show(response.data.data);
        this.load();
      });
    },
    saveAi() {
      ApiClient.post("api/admin/crm/telegram-chats/settings", { ai_enabled: this.aiEnabled });
    },
    short(value) {
      if (!value) return "";
      const date = new Date(value);
      const today = date.toDateString() === new Date().toDateString();
      return today
        ? date.toLocaleTimeString("uk-UA", { hour: "2-digit", minute: "2-digit" })
        : date.toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit" });
    },
    human(value) {
      return value ? new Date(value).toLocaleString("uk-UA", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
    },
  },
};
</script>

<style lang="scss" scoped>
$teal: #009fb3;

.bot-chats {
  max-width: 1200px;
  margin: 0 auto;
  &__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px 24px;
    margin-bottom: 16px;
  }
  &__filter {
    flex: 0 1 220px;
  }
  &__search {
    flex: 0 1 260px;
  }
  &__panes {
    display: grid;
    grid-template-columns: 340px minmax(0, 1fr);
    height: 640px;
    border: 1px solid #ddd;
    border-radius: 8px;
    overflow: hidden;
  }
  &__list {
    margin: 0;
    padding: 0;
    overflow-y: auto;
    border-right: 1px solid #ddd;
    list-style: none;
  }
  &__empty,
  &__hint {
    padding: 24px;
    color: #888;
  }
  &__item {
    display: block;
    width: 100%;
    padding: 10px 14px;
    border-bottom: 1px solid #eee;
    text-align: left;
    &:hover {
      background: #f7f9fa;
    }
    &--active {
      background: #e8f6f8;
    }
  }
  &__line {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }
  &__name {
    overflow: hidden;
    font-weight: 500;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  &__date {
    flex: 0 0 auto;
    color: #888;
    font-size: 12px;
  }
  &__preview {
    overflow: hidden;
    color: #666;
    font-size: 13px;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  &__unread {
    flex: 0 0 auto;
    min-width: 20px;
    padding: 0 6px;
    border-radius: 10px;
    background: $teal;
    color: #fff;
    font-size: 12px;
    line-height: 20px;
    text-align: center;
  }
  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
  }
  &__tag {
    color: #888;
    font-size: 12px;
    &--human {
      color: #d9730d;
      font-weight: 500;
    }
  }
  &__dialog {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
  }
  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
    padding: 10px 14px;
    border-bottom: 1px solid #eee;
  }
  &__back {
    display: none;
  }
  &__who {
    display: flex;
    flex-direction: column;
    flex: 1 1 200px;
    min-width: 0;
  }
  &__actions {
    display: flex;
    flex-wrap: wrap;
  }
  &__messages {
    flex: 1 1 auto;
    min-height: 0;
    padding: 14px;
    overflow-y: auto;
    background: #f7f9fa;
  }
  &__msg {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-bottom: 12px;
    &--user {
      align-items: flex-start;
    }
    &--user &__bubble {
      background: #fff;
    }
    &--ai &__bubble {
      background: #e8f6f8;
    }
    &--admin &__bubble {
      background: #d8f0d8;
    }
    &--bot &__bubble {
      background: #eee;
    }
  }
  &__author {
    margin-bottom: 2px;
    color: #888;
    font-size: 12px;
  }
  &__bubble {
    max-width: 80%;
    padding: 8px 12px;
    border: 1px solid #e3e3e3;
    border-radius: 10px;
    white-space: pre-wrap;
    word-break: break-word;
  }
  &__operator {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 12px;
    margin-bottom: 16px;
    padding: 10px 14px;
    border: 1px solid #e3eef0;
    border-radius: 8px;
    background: #f7fbfc;
  }
  &__operator-link {
    flex: 1 1 100%;
    margin: 0;
    word-break: break-all;
  }
  &__msg-actions {
    display: flex;
    gap: 12px;
    margin-top: 2px;
    button {
      color: $teal;
      font-size: 12px;
      &:hover {
        text-decoration: underline;
      }
    }
  }
  &__bubble--deleted {
    color: #999;
    text-decoration: line-through;
  }
  &__editing {
    margin: 0;
    padding: 6px 14px;
    background: #fff8e6;
    color: #8a6100;
    font-size: 13px;
    button {
      margin-left: 8px;
      color: $teal;
      text-decoration: underline;
    }
  }
  &__paused {
    margin: 0;
    padding: 6px 14px;
    color: #888;
    font-size: 13px;
  }
  &__composer {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    padding: 10px 14px;
    border-top: 1px solid #eee;
  }
  &__input {
    flex: 1 1 auto;
    max-height: 160px;
    overflow-y: auto;
  }
}

@media (max-width: 900px) {
  .bot-chats {
    &__panes {
      grid-template-columns: minmax(0, 1fr);
      height: 70vh;
      min-height: 480px;
    }
    &__list {
      border-right: 0;
    }
    &__dialog {
      display: none;
    }
    &__panes--open &__list {
      display: none;
    }
    &__panes--open &__dialog {
      display: flex;
    }
    &__back {
      display: inline-flex;
    }
    &__filter,
    &__search {
      flex: 1 1 100%;
    }
    // iOS zooms the page on a smaller field
    ::v-deep input,
    ::v-deep textarea {
      font-size: 16px;
    }
  }
}
</style>
